/**
 * Composer attachment chips for the agent chat page (#1273 D1).
 *
 * Ticking a table in the side panel's Tables tab, or a variable in its
 * Variables tab, pins it to the composer as a chip — it never sends. On send,
 * the chips are appended to the user's text as fixed trailing blocks, one block
 * per kind, tables first:
 *
 *   <what the user typed>
 *
 *   Tables to use (from the table of contents):
 *   - Brand/Awareness (job: sky-uk-be-prius)
 *
 *   Variables to use (from the variable tree):
 *   - Demographics/Age (job: sky-uk-be-prius)
 *
 * 🔴 The blocks say WHAT is attached and nothing about what to do with it.
 * That is deliberate: the instruction is whatever the user typed in the message
 * box. A chip is an attachment, not a command — so never word these headers as
 * one, and never let a chip imply an action the user did not ask for.
 *
 * The wording and the layout are the contract: the agent's prompt looks for
 * these exact headers, so change them here, in docs/agent.md, and in the
 * agent image's CLAUDE.md together or not at all.
 *
 * Chips are deliberately not editable — remove one and add another.
 */
import { useCallback, useMemo, useState } from 'react'

/** What a chip points at. Blocks are emitted in this order. */
export type ChipKind = 'table' | 'variable' | 'dashboard'

/** The header line the persona matches on, per kind. */
export const TABLE_CHIP_BLOCK_HEADER = 'Tables to use (from the table of contents):'
export const VARIABLE_CHIP_BLOCK_HEADER = 'Variables to use (from the variable tree):'
export const DASHBOARD_CHIP_BLOCK_HEADER = 'Dashboards to use (from DataViz):'

const BLOCK_HEADERS: Record<ChipKind, string> = {
  table: TABLE_CHIP_BLOCK_HEADER,
  variable: VARIABLE_CHIP_BLOCK_HEADER,
  dashboard: DASHBOARD_CHIP_BLOCK_HEADER,
}

/** Emission order of the blocks, independent of the order chips were added. */
const KIND_ORDER: ChipKind[] = ['table', 'variable', 'dashboard']

export interface ComposerChip {
  /** Stable identity: kind + job + job-relative path. */
  id: string
  kind: ChipKind
  /** Chip face — the display name. */
  label: string
  /** Hover text — the full path the label was shortened from, plus the job. */
  tooltip: string
  /** Job-relative `folderPath/name`, or just `name` at the tree root. */
  path: string
  job: string
}

function buildChip(
  kind: ChipKind,
  displayName: string,
  folderPath: string,
  name: string,
  job: string,
  description?: string,
  jobLabel?: string,
): ComposerChip {
  // Backslashes normalised, as goapi's mergeMetadataIntoNodes does when it
  // builds the index key: a TOC can carry them, and a path holding one matches
  // no blob and no index entry. The `.cbt` suffix is deliberately NOT added —
  // loadCBTFile appends it when missing, and it is the single door both the
  // agent's render_table and this app's preview go through.
  const path = (folderPath ? `${folderPath}/${name}` : name).replace(/\\/g, '/')
  // The job's display name in anything a person reads (Matt, 10 Sep: never
  // the codename in the UI); `job` itself stays the codename because it is
  // what the agent's tools take.
  const where = `${path} (job: ${jobLabel || job})`
  return {
    id: `${kind}:${job}::${path}`,
    kind,
    label: displayName || name,
    // The chip face is deliberately short — the descriptive label the tree
    // shows (a variable's question text, say) would not fit and would make a
    // row of chips unreadable. It goes here instead, so hovering a chip tells
    // you what it actually is as well as where it came from.
    tooltip: description ? `${description} — ${where}` : where,
    path,
    job,
  }
}

/** Build a chip from a table-of-contents node's parts. */
export function buildTableChip({
  displayName,
  folderPath,
  filename,
  job,
  jobLabel,
}: {
  displayName: string
  folderPath: string
  filename: string
  job: string
  /** The job's display name, for the hover text. */
  jobLabel?: string
}): ComposerChip {
  return buildChip('table', displayName, folderPath, filename, job, undefined, jobLabel)
}

/**
 * Build a chip from a variable-tree node's parts.
 *
 * The chip face is the variable's NAME, not the label the tree renders — a
 * vartree label is `age - What is your age?`, which is a sentence, not a chip.
 * Pass that sentence as `description` and it becomes the hover text.
 */
export function buildVariableChip({
  name,
  folderPath,
  job,
  description,
  jobLabel,
}: {
  name: string
  folderPath: string
  job: string
  /** The variable's question text, if it has one. Hover text only. */
  description?: string
  /** The job's display name, for the hover text. */
  jobLabel?: string
}): ComposerChip {
  return buildChip('variable', name, folderPath, name, job, description, jobLabel)
}

/**
 * Build a chip for a DataViz dashboard. `path` carries the dashboard's ID —
 * the one thing the agent needs to fetch it (`get_dashboard`) — and the chip
 * face is its name. Owner and tier are hover text.
 */
export function buildDashboardChip({
  id,
  name,
  job,
  description,
  jobLabel,
}: {
  id: string
  name: string
  job: string
  /** e.g. "Company-level · matt@bayesprice.com". Hover text only. */
  description?: string
  /** The job's display name, for the hover text. */
  jobLabel?: string
}): ComposerChip {
  const shown = jobLabel || job
  return {
    id: `dashboard:${job}::${id}`,
    kind: 'dashboard',
    label: name,
    tooltip: description ? `${description} — job ${shown}` : `job ${shown}`,
    path: id,
    job,
  }
}

/** One attachment line. A dashboard is named by its ID, which is what the
 *  agent's `get_dashboard` takes; its name rides along for the reader. */
function chipLine(c: ComposerChip): string {
  if (c.kind === 'dashboard') return `- ${c.label} (dashboard id: ${c.path}, job: ${c.job})`
  return `- ${c.path} (job: ${c.job})`
}

/** The exact text sent for a message with chips attached. */
export function buildChippedMessage(content: string, chips: ComposerChip[]): string {
  if (chips.length === 0) return content
  // One block per kind that has chips, in KIND_ORDER, chips in chip order
  // within a block. A kind with nothing attached emits no header at all —
  // an empty heading reads as "none available" rather than "none picked".
  const blocks = KIND_ORDER.flatMap(kind => {
    const of = chips.filter(c => c.kind === kind)
    if (of.length === 0) return []
    return [`${BLOCK_HEADERS[kind]}\n${of.map(chipLine).join('\n')}`]
  })
  return `${content}\n\n${blocks.join('\n\n')}`
}

/**
 * The opening line offered when something is attached and the box is empty.
 *
 * 🔴 A suggestion, not an instruction. The blocks say what is attached; this
 * says the most likely thing to do with it, in the user's own message box
 * where they can rewrite or delete it. It is never forced on typed text —
 * see AgentComposerConfig.suggestedText.
 *
 * The verb is the same for both kinds on purpose. "Tabulate these variables"
 * would put an analysis in the user's mouth that they did not ask for.
 */
export function suggestedPromptFor(chips: ComposerChip[]): string {
  const hasTables = chips.some(c => c.kind === 'table')
  const hasVariables = chips.some(c => c.kind === 'variable')
  const hasDashboards = chips.some(c => c.kind === 'dashboard')
  // A dashboard alongside anything else has no single obvious verb: offer
  // nothing and let the user say what they want done with the mix.
  if (hasDashboards) return hasTables || hasVariables ? '' : 'Please look at these dashboards'
  if (hasTables && hasVariables) return 'Please render these tables and variables'
  if (hasTables) return 'Please render these tables'
  if (hasVariables) return 'Please render these variables'
  return ''
}

export default function useComposerChips() {
  const [chips, setChips] = useState<ComposerChip[]>([])

  const addChip = useCallback((chip: ComposerChip) => {
    setChips(prev => (prev.some(c => c.id === chip.id) ? prev : [...prev, chip]))
  }, [])

  const removeChip = useCallback((id: string) => {
    setChips(prev => prev.filter(c => c.id !== id))
  }, [])

  const toggleChip = useCallback((chip: ComposerChip, on: boolean) => {
    setChips(prev => {
      const has = prev.some(c => c.id === chip.id)
      if (on) return has ? prev : [...prev, chip]
      return has ? prev.filter(c => c.id !== chip.id) : prev
    })
  }, [])

  const clear = useCallback(() => setChips([]), [])

  const chipIds = useMemo(() => new Set(chips.map(c => c.id)), [chips])
  const isChipped = useCallback((id: string) => chipIds.has(id), [chipIds])

  const buildMessage = useCallback(
    (content: string) => buildChippedMessage(content, chips),
    [chips],
  )

  const suggestedPrompt = useMemo(() => suggestedPromptFor(chips), [chips])

  return { chips, chipIds, addChip, removeChip, toggleChip, clear, isChipped, buildMessage, suggestedPrompt }
}
